import React, { useCallback } from 'react';
import { format as formatDate, differenceInSeconds } from 'date-fns';
import { IconInfo, IconSmallCheckmark } from 'src/components/Icons';
import { Body, Button, Heading, Pill } from 'src/components/UI';
import styles from './styles.module.scss';

interface CouponProps {
    title: string;
    imageSrc?: string;
    description: string;
    isClipped: boolean;
    expirationDate: string;
    isRedeemed: boolean;
    channel: 'all' | 'store' | 'online';
    onClip: (event: React.MouseEvent) => void;
}
function Coupon({
    title,
    imageSrc,
    description,
    isClipped,
    expirationDate,
    isRedeemed,
    channel,
    onClip,
}: CouponProps) {
    const ChannelComponent = useCallback(() => {
        switch (channel) {
            case 'online':
                return (
                    <Body size={12} weight="bold">
                        Online only
                    </Body>
                );
            case 'store':
                return (
                    <Body size={12} weight="bold">
                        In store only
                    </Body>
                );
            default:
                return null;
        }
    }, [channel]);

    const BadgeComponent = useCallback(() => {
        const currentDateObj = new Date();
        const expirationDateObj = new Date(expirationDate);

        const differenceInDate = differenceInSeconds(
            expirationDateObj,
            currentDateObj
        );

        if (differenceInDate <= 86_400) {
            return <Pill className={styles.couponPill}>EXPIRES TODAY</Pill>;
        }
        if (differenceInDate <= 172_800) {
            return <Pill className={styles.couponPill}>2 DAYS LEFT</Pill>;
        }
        if (differenceInDate <= 259_200) {
            return <Pill className={styles.couponPill}>3 DAYS LEFT</Pill>;
        }
        if (differenceInDate <= 604_800) {
            return <Pill className={styles.couponPill}>THIS WEEK ONLY</Pill>;
        }
        return null;
    }, [expirationDate]);

    return (
        <div className={styles.couponContainer}>
            <span className={styles.couponInfoButton}>
                <IconInfo />
            </span>
            <div>
                <BadgeComponent />
                <div className={styles.couponContentContainer}>
                    {imageSrc && (
                        <img
                            className={styles.couponImage}
                            src={imageSrc}
                            alt="offer"
                        />
                    )}
                    <div>
                        {!imageSrc && <Body size={14}>Just for you</Body>}
                        <Heading
                            type="h6"
                            size={26}
                            color="textRed"
                            weight="bold"
                        >
                            {title}
                        </Heading>
                        <Body size={14} weight="light">
                            {description}
                        </Body>
                    </div>
                </div>
            </div>
            <div className={styles.couponActionsContainer}>
                <div>
                    <ChannelComponent />
                    <Body size={12} weight="light">
                        Exp {formatDate(new Date(expirationDate), 'M/d/yy')}
                    </Body>
                </div>
                {!isRedeemed && !isClipped ? (
                    <Button
                        bgColor="bgRed"
                        size="medium"
                        onClick={onClip}
                        fontSize={12}
                    >
                        Clip
                    </Button>
                ) : (
                    <Body
                        className={styles.clippedButtonContent}
                        size={12}
                        weight="bold"
                        color="textRed"
                    >
                        <IconSmallCheckmark
                            height={16}
                            width={16}
                            bgFillColor="fillRed"
                        />{' '}
                        Applies at register
                    </Body>
                )}
            </div>
        </div>
    );
}

export default Coupon;
